/**
 * Datos de la app. Todo se lee y se escribe en Notion a través de /api/data.
 *
 * Sincronización (explícita):
 *  - App → Notion: cada guardado espera la confirmación de Notion y muestra el registro guardado.
 *  - Notion → App: se vuelve a consultar al abrir un módulo, al volver a la pestaña,
 *    cada 60 segundos mientras está abierto, y con el botón "Actualizar".
 *  - Conflictos: si alguien editó el registro en Notion mientras lo editabas, el servidor
 *    responde 409 y la app ofrece recargar o sobrescribir.
 *
 * En modo demostración se usa un almacén local con datos de ejemplo (nada sale del navegador).
 */
import { useEffect, useSyncExternalStore } from 'react'
import type { CollectionKey, Rec } from '../../shared/schema'
import { DEMO, storage } from './config'
import { sessionExpired } from './auth'
import { demoSeed } from './demo-data'

export class ConflictError extends Error {
  constructor(public current: Rec) { super('Conflicto') }
}

/* ------------------------------ transporte ------------------------------ */

async function http<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response
  try {
    res = await fetch(path, {
      ...init,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
    })
  } catch {
    throw new Error('Sin conexión. Revisá internet y probá de nuevo.')
  }
  const data = await res.json().catch(() => ({}))
  if (res.status === 401) { sessionExpired(); throw new Error(data.error ?? 'Tu sesión venció.') }
  if (res.status === 409) throw new ConflictError(data.current)
  if (!res.ok) throw new Error(data.error ?? `Error ${res.status}`)
  return data as T
}

/* ----------------------------- modo demo ----------------------------- */

const DEMO_KEY = 'revela.demo.v3'
let demoDb: Record<string, Rec[]> | null = null
function db() {
  if (!demoDb) {
    try { demoDb = JSON.parse(storage.get(DEMO_KEY) || 'null') } catch { demoDb = null }
    if (!demoDb) demoDb = demoSeed()
  }
  return demoDb!
}
const persist = () => storage.set(DEMO_KEY, JSON.stringify(demoDb))
const uid = () => (crypto.randomUUID?.() ?? Math.random().toString(36).slice(2) + Date.now().toString(36))
const counters: Record<string, string> = { propiedades: 'PROP', clientes: 'CLI', visitas: 'VIS', marketing: 'MKT', academia: 'ACA', documentos: 'DOC', tareas: 'TAR', evaluaciones: 'EVA' }
const wait = (ms = 180) => new Promise(r => setTimeout(r, ms))

export function resetDemo() { demoDb = demoSeed(); persist(); for (const k of Object.keys(cache)) refresh(k as CollectionKey) }

const backend = {
  async list(col: CollectionKey): Promise<Rec[]> {
    if (DEMO) { await wait(); return structuredClone(db()[col] ?? []) }
    return http<Rec[]>(`/api/data?col=${col}`)
  },
  async create(col: CollectionKey, data: Record<string, any>): Promise<Rec> {
    if (DEMO) {
      await wait()
      const list = (db()[col] ??= [])
      const n = list.reduce((m, r) => Math.max(m, Number(String(r.ref ?? '').split('-')[1]) || 0), 0) + 1
      const now = new Date().toISOString()
      const rec: Rec = { ...data, id: uid(), ref: `${counters[col]}-${n}`, createdAt: now, updatedAt: now }
      if (col === 'visitas' && !rec.recordatorio) rec.recordatorio = 'Desactivado'
      list.unshift(rec); persist(); return structuredClone(rec)
    }
    return http<Rec>(`/api/data?col=${col}`, { method: 'POST', body: JSON.stringify({ data }) })
  },
  async update(col: CollectionKey, id: string, data: Record<string, any>, updatedAt?: string, force = false): Promise<Rec> {
    if (DEMO) {
      await wait()
      const list = db()[col] ?? []
      const i = list.findIndex(r => r.id === id)
      if (i < 0) throw new Error('No se encontró el registro.')
      list[i] = { ...list[i], ...data, updatedAt: new Date().toISOString() }
      persist(); return structuredClone(list[i])
    }
    return http<Rec>(`/api/data?col=${col}&id=${id}`, { method: 'PATCH', body: JSON.stringify({ data, updatedAt, force }) })
  },
  async remove(col: CollectionKey, id: string): Promise<void> {
    if (DEMO) { await wait(); db()[col] = (db()[col] ?? []).filter(r => r.id !== id); persist(); return }
    await http(`/api/data?col=${col}&id=${id}`, { method: 'DELETE' })
  },
}

/* ------------------------------ caché ------------------------------ */

interface Entry { data: Rec[]; loading: boolean; error: string | null; fetchedAt: number; subs: number }
const cache: Partial<Record<CollectionKey, Entry>> = {}
const listeners = new Set<() => void>()
let version = 0
const emit = () => { version++; listeners.forEach(l => l()) }
const entry = (col: CollectionKey) => (cache[col] ??= { data: [], loading: false, error: null, fetchedAt: 0, subs: 0 })

const inflight: Partial<Record<CollectionKey, Promise<void>>> = {}
export function refresh(col: CollectionKey): Promise<void> {
  if (inflight[col]) return inflight[col]!
  const e = entry(col)
  e.loading = true; emit()
  inflight[col] = backend.list(col)
    .then(data => { e.data = data; e.error = null; e.fetchedAt = Date.now() })
    .catch(err => { e.error = err.message })
    .finally(() => { e.loading = false; delete inflight[col]; emit() })
  return inflight[col]!
}

export function refreshAll() {
  return Promise.all((Object.keys(cache) as CollectionKey[]).map(refresh))
}

// Notion → App: al volver a la pestaña y cada 60 s
if (typeof window !== 'undefined') {
  window.addEventListener('focus', () => {
    for (const [k, e] of Object.entries(cache)) if (e!.subs > 0 && Date.now() - e!.fetchedAt > 15000) refresh(k as CollectionKey)
  })
  setInterval(() => {
    if (document.visibilityState !== 'visible') return
    for (const [k, e] of Object.entries(cache)) if (e!.subs > 0) refresh(k as CollectionKey)
  }, 60000)
}

export function useRecords(col: CollectionKey) {
  useSyncExternalStore(cb => { listeners.add(cb); return () => listeners.delete(cb) }, () => version)
  useEffect(() => {
    const e = entry(col)
    e.subs++
    if (!e.fetchedAt || Date.now() - e.fetchedAt > 20000) refresh(col)
    return () => { e.subs-- }
  }, [col])
  const e = entry(col)
  return { data: e.data, loading: e.loading && !e.fetchedAt, refreshing: e.loading, error: e.error, refresh: () => refresh(col) }
}

export function useSyncState() {
  useSyncExternalStore(cb => { listeners.add(cb); return () => listeners.delete(cb) }, () => version)
  const entries = Object.values(cache)
  return {
    busy: entries.some(e => e!.loading),
    lastSync: Math.max(0, ...entries.map(e => e!.fetchedAt)),
    error: entries.find(e => e!.error)?.error ?? null,
  }
}

export async function createRecord(col: CollectionKey, data: Record<string, any>) {
  const rec = await backend.create(col, data)
  const e = entry(col)
  e.data = [rec, ...e.data]; emit()
  return rec
}

export async function updateRecord(col: CollectionKey, rec: Rec, patch: Record<string, any>, opts: { force?: boolean } = {}) {
  const saved = await backend.update(col, rec.id, patch, rec.updatedAt, opts.force)
  const e = entry(col)
  e.data = e.data.map(r => (r.id === rec.id ? saved : r)); emit()
  return saved
}

/** Cambio rápido (arrastrar en kanban/calendario): se ve al instante y se revierte si falla */
export async function quickUpdate(col: CollectionKey, rec: Rec, patch: Record<string, any>) {
  const e = entry(col)
  const before = e.data
  e.data = e.data.map(r => (r.id === rec.id ? { ...r, ...patch } : r)); emit()
  try {
    return await updateRecord(col, rec, patch, { force: true })
  } catch (err) {
    e.data = before; emit(); throw err
  }
}

export async function deleteRecord(col: CollectionKey, id: string) {
  await backend.remove(col, id)
  const e = entry(col)
  e.data = e.data.filter(r => r.id !== id); emit()
}

/* ------------------------------ archivos ------------------------------ */

async function compressImage(file: File): Promise<Blob> {
  if (!file.type.startsWith('image/') || file.size < 900_000) return file
  try {
    const bmp = await createImageBitmap(file)
    const scale = Math.min(1, 2000 / Math.max(bmp.width, bmp.height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(bmp.width * scale); canvas.height = Math.round(bmp.height * scale)
    canvas.getContext('2d')!.drawImage(bmp, 0, 0, canvas.width, canvas.height)
    return await new Promise(res => canvas.toBlob(b => res(b ?? file), 'image/jpeg', 0.84))
  } catch { return file }
}

/** Sube un archivo a Cloudflare y devuelve el enlace permanente que se guarda en Notion */
export async function uploadFile(file: File, _bucket: 'public' | 'private', folder: string): Promise<string> {
  const blob = await compressImage(file)
  if (DEMO) {
    if (blob.size > 2_500_000) throw new Error('En la demostración, usá archivos de menos de 2,5 MB.')
    return await new Promise((res, rej) => {
      const r = new FileReader(); r.onload = () => res(String(r.result)); r.onerror = () => rej(new Error('No se pudo leer el archivo.')); r.readAsDataURL(blob)
    })
  }
  if (blob.size > 10 * 1024 * 1024) throw new Error('El archivo supera los 10 MB. Comprimilo o subilo a Drive y pegá el enlace.')
  const qs = new URLSearchParams({ carpeta: folder, nombre: file.name })
  let res: Response
  try {
    res = await fetch(`/api/archivos?${qs}`, { method: 'PUT', credentials: 'same-origin', headers: { 'Content-Type': blob.type || 'application/octet-stream' }, body: blob })
  } catch { throw new Error('Sin conexión. No se pudo subir el archivo.') }
  const data = await res.json().catch(() => ({}))
  if (res.status === 401) { sessionExpired(); throw new Error('Tu sesión venció.') }
  if (!res.ok) throw new Error(data.error ?? 'No se pudo subir el archivo. Probá de nuevo.')
  return data.value
}

/** Enlace para abrir un archivo (los de la app se abren con la sesión iniciada) */
export async function openFileUrl(value: string): Promise<string> {
  return value
}

export async function fetchStatus() {
  if (DEMO) return null
  return http<any>('/api/status')
}

/** Crea o completa las bases en Notion, una colección por pedido, y avisa el avance */
export async function runNotionSetup(onStep: (msg: string) => void) {
  const cols = ['propiedades', 'clientes', 'visitas', 'marketing', 'academia', 'documentos', 'tareas', 'evaluaciones']
  for (const col of cols) {
    const r = await http<{ db: string; action: string }>('/api/setup', { method: 'POST', body: JSON.stringify({ paso: 'base', col }) })
    onStep(`${r.db}: ${r.action}`)
  }
  for (const col of cols) {
    const r = await http<{ db: string; added: number }>('/api/setup', { method: 'POST', body: JSON.stringify({ paso: 'relaciones', col }) })
    if (r.added) onStep(`${r.db}: ${r.added} relación${r.added > 1 ? 'es' : ''} agregada${r.added > 1 ? 's' : ''}`)
  }
  onStep('Listo. Las 8 bases están en Notion.')
}
